/**
 * Enhanced Toast Service with Loading-Aware Queue
 * 
 * Extends the existing toastDeduplicationService to support queuing toasts
 * during loading states, ensuring smooth UX during BlendingSpinner display.
 * 
 * This integrates with existing patterns:
 * - Maintains existing toastDeduplicationService logic
 * - Uses useMasterLoading for loading state detection
 * - Follows react-hot-toast patterns
 * - Compatible with existing event handlers in App.tsx
 */

import { toast } from 'react-hot-toast';
import { toastDeduplicationService, ToastEvent } from './toastDeduplicationService';
import { useUiStore } from '../store/uiStore';

// Enhanced interface for queued toasts
interface QueuedToast {
  id: string;
  content: string | React.ReactElement;
  options?: any;
  event?: ToastEvent;
  timestamp: number;
  isRecovery: boolean;
}

class EnhancedToastService {
  private queuedToasts: QueuedToast[] = [];
  private isMonitoring = false;
  private isInitialized = false;

  /**
   * Initialize the service (optional - service works without explicit initialization)
   */
  initialize(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    console.log('🍞 Enhanced Toast Service initialized with loading awareness');
  }

  /**
   * Show toast with loading awareness (replaces direct toast() calls)
   * @param content Toast content (string or React element)
   * @param options Toast options (duration, style, etc.)
   * @param event Optional event for deduplication
   * @param isRecovery Whether this is a recovery/while-away toast
   */
  showToast(
    content: string | React.ReactElement, 
    options: any = {}, 
    event?: ToastEvent, 
    isRecovery = false
  ): void {
    // Deduplicate first if event is provided
    if (event && !toastDeduplicationService.shouldShowToast(event)) {
      console.log(`🔄 Toast deduplicated: ${event.type} for ${event.symbol}`);
      return;
    }

    // Check if loading is active
    if (this.isLoadingActive()) {
      this.queueToast(content, options, event, isRecovery);
      this.startMonitoring();
      return;
    }

    // Show immediately if not loading
    if (content) {
      toast(content, options);
    }
  }

  /**
   * Queue toast for later display during loading
   */
  private queueToast(
    content: string | React.ReactElement, 
    options: any, 
    event?: ToastEvent, 
    isRecovery = false
  ): void {
    const queuedToast: QueuedToast = {
      id: `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content,
      options,
      event,
      timestamp: Date.now(),
      isRecovery
    };

    this.queuedToasts.push(queuedToast);
    
    console.log(`🔄 Toast queued during loading: ${isRecovery ? 'recovery' : 'live'} (${this.queuedToasts.length} total)`);
  }

  /**
   * Check if loading is currently active
   */
  private isLoadingActive(): boolean {
    try {
      const { masterLoadingState } = useUiStore.getState();
      const isActive = masterLoadingState.isActive && 
                      masterLoadingState.stage !== 'css' && 
                      masterLoadingState.stage !== 'complete';
      
      return isActive;
    } catch (error) {
      console.warn('⚠️ Could not check loading state, showing toast immediately:', error);
      return false; // Fail gracefully - show toast immediately
    }
  }

  /**
   * Start monitoring loading state to flush queue when ready
   */
  private startMonitoring(): void {
    if (this.isMonitoring) return;
    this.isMonitoring = true;

    const checkAndFlush = () => {
      if (!this.isLoadingActive() && this.queuedToasts.length > 0) {
        // Loading completed, flush the queue
        this.flushQueue();
        this.isMonitoring = false;
      } else if (this.queuedToasts.length > 0) {
        // Still loading, check again in 500ms
        setTimeout(checkAndFlush, 500);
      } else {
        // No toasts in queue
        this.isMonitoring = false;
      }
    };

    // Start checking after a small delay
    setTimeout(checkAndFlush, 500);
  }

  /**
   * Flush all queued toasts with smart timing and prioritization
   */
  private flushQueue(): void {
    if (this.queuedToasts.length === 0) return;

    console.log(`🚀 Flushing ${this.queuedToasts.length} queued toasts`);

    // Sort by priority: recovery events first, then by timestamp (oldest first)
    const sortedToasts = [...this.queuedToasts].sort((a, b) => {
      // Recovery events have higher priority
      if (a.isRecovery && !b.isRecovery) return -1;
      if (b.isRecovery && !a.isRecovery) return 1;
      
      // Then by timestamp (oldest first)
      return a.timestamp - b.timestamp;
    });

    // Show toasts with staggered timing for better UX
    sortedToasts.forEach((queuedToast, index) => {
      setTimeout(() => {
        // Final deduplication check before showing
        if (queuedToast.event && !toastDeduplicationService.shouldShowToast(queuedToast.event)) {
          console.log(`🔄 Queued toast deduplicated on flush: ${queuedToast.event.type}`);
          return;
        }

        // Show the toast with optional recovery indicator
        if (queuedToast.content) {
          toast(queuedToast.content, {
            ...queuedToast.options,
            // Add visual indicator for recovery events
            ...(queuedToast.isRecovery && {
              icon: '📋',
              style: {
                ...queuedToast.options?.style,
                border: '1px solid rgba(59, 130, 246, 0.3)', // Blue border for recovery
              }
            })
          });
        }
      }, index * 200); // 200ms between toasts for smooth appearance
    });

    // Clear the queue
    this.queuedToasts = [];
  }

  /**
   * Manual flush for testing or special cases
   */
  flushQueueManually(): void {
    this.flushQueue();
  }

  /**
   * Get queue stats for debugging
   */
  getQueueStats(): { 
    queueLength: number; 
    recoveryEvents: number; 
    liveEvents: number;
    isMonitoring: boolean;
  } {
    return {
      queueLength: this.queuedToasts.length,
      recoveryEvents: this.queuedToasts.filter(t => t.isRecovery).length,
      liveEvents: this.queuedToasts.filter(t => !t.isRecovery).length,
      isMonitoring: this.isMonitoring
    };
  }

  /**
   * Clear all queued toasts (for cleanup or testing)
   */
  clearQueue(): void {
    this.queuedToasts = [];
    this.isMonitoring = false;
    console.log('🧹 Toast queue cleared');
  }
}

// Global instance that follows existing patterns
export const enhancedToastService = new EnhancedToastService();

/**
 * Convenience wrapper that maintains API compatibility with existing toast calls
 * Use this as a drop-in replacement for direct toast() calls
 */
export const showToastWhenReady = (
  content: string | React.ReactElement, 
  options: any = {},
  event?: ToastEvent,
  isRecovery = false
) => {
  enhancedToastService.showToast(content, options, event, isRecovery);
};

/**
 * Legacy compatibility layer for common toast patterns
 * Allows gradual migration from direct toast() calls
 */
export const toastWithLoadingAwareness = {
  success: (message: string, options?: any) => 
    enhancedToastService.showToast(message, { 
      ...options, 
      style: { background: '#10b981', color: '#fff', ...options?.style } 
    }),
  
  error: (message: string, options?: any) => 
    enhancedToastService.showToast(message, { 
      ...options, 
      style: { background: '#ef4444', color: '#fff', ...options?.style } 
    }),
  
  info: (message: string, options?: any) => 
    enhancedToastService.showToast(message, { 
      ...options, 
      style: { background: '#3b82f6', color: '#fff', ...options?.style } 
    }),
  
  custom: (content: string | React.ReactElement, options?: any, event?: ToastEvent, isRecovery = false) =>
    enhancedToastService.showToast(content, options, event, isRecovery)
};

// Export types for use in other files
export type { QueuedToast };